import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { Plus, User, Mail, Phone, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import MethodSubjectField from '@/components/MethodSubjectField';

const BookSeat = () => {
  const [searchParams] = useSearchParams();
  const editBookingId = searchParams.get('edit');
  const [courses, setCourses] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [bookingAmountSettings, setBookingAmountSettings] = useState([]);
  const [amountManuallyEdited, setAmountManuallyEdited] = useState(false);
  const [feeManuallyEdited, setFeeManuallyEdited] = useState(false);
  const [isCustomFee, setIsCustomFee] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    booking_id: '',
    receipt_no: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp_no: '',
    course_id: '',
    method_subject: '',
    booking_amount: 0,
    payment_mode: '',
    payment_modes: [],
    consultant_id: '',
    consultant_payout: 0,
    session: '',
    session_id: '',
    is_lateral_entry: false,
    entry_type: 'Regular',
    entry_semester: null,
    booking_date: new Date().toISOString().split('T')[0]
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
    fetchConsultants();
    fetchBookingAmountSettings();
    fetchSessions();
    generateBookingId();
  }, []);

  // Fetch booking data after sessions are loaded (for edit mode)
  useEffect(() => {
    if (editBookingId && sessions.length > 0) {
      fetchBookingData(editBookingId);
    }
  }, [editBookingId, sessions]);

  const generateBookingId = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const randomBooking = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const bookingId = `BKG${year}${randomBooking}`;
    setFormData(prev => ({ ...prev, booking_id: bookingId, receipt_no: '' }));
    console.log('Generated booking ID:', bookingId);
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultants/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Consultants loaded:', response.data);
      const consultantsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setConsultants(consultantsData.filter(c => !c.is_blocked));
    } catch (error) {
      console.error('Error fetching consultants:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
      // Set empty array so form still works
      setConsultants([]);
    }
  };

  const fetchBookingAmountSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/seat-booking/amount-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookingAmountSettings(response.data);
      console.log('Booking amount settings loaded:', response.data);
    } catch (error) {
      console.error('Error fetching booking amount settings:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
      console.log('Sessions loaded:', response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchBookingData = async (bookingId) => {
    try {
      console.log('Fetching booking data for ID:', bookingId);
      const response = await axios.get(`${API_URL}/api/seat-booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const booking = response.data;
      console.log('Booking data loaded:', booking);
      
      // Find session_id from session name if not present
      let sessionId = booking.session_id || '';
      let sessionName = booking.session || '';
      
      if (!sessionId && sessionName && sessions.length > 0) {
        const matchedSession = sessions.find(s => s.name === sessionName);
        if (matchedSession) {
          sessionId = matchedSession.id;
          console.log('✅ Matched session_id from name:', sessionId);
        }
      }
      
      // If still no session, try to use current/active session
      if (!sessionId && sessions.length > 0) {
        const currentSession = sessions.find(s => s.is_current) || sessions[0];
        if (currentSession) {
          sessionId = currentSession.id;
          sessionName = currentSession.name;
          console.log('✅ Using current session as fallback:', sessionName);
        }
      }
      
      // Pre-fill form with booking data
      setFormData({
        first_name: booking.first_name || '',
        middle_name: booking.middle_name || '',
        last_name: booking.last_name || '',
        email: booking.email || '',
        phone: booking.phone || '',
        whatsapp_no: booking.whatsapp_no || booking.phone || '',
        course_id: booking.course_id || '',
        booking_amount: booking.booking_amount || 0,
        course_fee: booking.course_fee || 0,
        payment_mode: booking.payment_mode || '',
        payment_modes: booking.payment_modes || [],
        consultant_id: booking.consultant_id || '',
        session: sessionName,
        session_id: sessionId,
        is_lateral_entry: booking.is_lateral_entry || false,
        entry_type: booking.is_lateral_entry ? 'Lateral' : 'Regular',
        entry_semester: booking.entry_semester || null
      });
      setIsEditMode(true);
      setAmountManuallyEdited(true);  // Edit mode: respect stored amount, don't overwrite
      setFeeManuallyEdited(true);  // Edit mode: respect stored course fee, don't overwrite
      setIsCustomFee(booking.is_custom_fee || false);
    } catch (error) {
      console.error('Error fetching booking data:', error);
      alert('Failed to load booking data. Please try again.');
    }
  };

  // Reusable function to fetch course fee from fee_structures, matching session
  const fetchCourseFeeForSession = async (courseId, sessionId, isLateral) => {
    if (!courseId) return 0;
    try {
      const feeRes = await axios.get(`${API_URL}/api/master-settings/fee-structures`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { course_id: courseId }
      });
      const feeData = Array.isArray(feeRes.data) ? feeRes.data : (feeRes.data?.data || []);
      if (feeData.length === 0) return 0;

      // Filter by entry type
      let filtered = feeData.filter(f => {
        if (isLateral && f.is_lateral_entry === false) return false;
        if (!isLateral && f.is_lateral_entry === true) return false;
        return true;
      });
      if (filtered.length === 0) filtered = feeData;

      // Try matching by session_id first
      if (sessionId) {
        const sessionMatch = filtered.find(f => f.session_id === sessionId);
        if (sessionMatch) {
          const fee = sessionMatch.total_fee || sessionMatch.total_amount || 0;
          console.log(`Matched fee for session ${sessionId}: ₹${fee}`);
          return fee;
        }
      }

      // Fallback: sort descending and pick first
      filtered.sort((a, b) => (b.total_fee || b.total_amount || 0) - (a.total_fee || a.total_amount || 0));
      return filtered[0]?.total_fee || filtered[0]?.total_amount || 0;
    } catch (err) {
      console.error('Error fetching fee structures:', err);
      return 0;
    }
  };

  const handleCourseChange = async (courseId) => {
    // Find booking amount for selected course based on entry type
    const entryType = formData.entry_type || 'Regular';
    const isLateral = entryType === 'Lateral';
    
    // Find booking amount setting for the course
    const amountSetting = bookingAmountSettings.find(setting => setting.course_id === courseId);
    
    // Use new regular_amount/lateral_amount fields, fallback to old amount field
    let bookingAmount = 0;
    if (amountSetting) {
      if (isLateral) {
        bookingAmount = amountSetting.lateral_amount || amountSetting.amount || 0;
      } else {
        bookingAmount = amountSetting.regular_amount || amountSetting.amount || 0;
      }
    }
    
    // Auto-select active session for this course
    const courseSessions = filterSessionsByCourse(sessions, courseId);
    const activeSession = courseSessions.find(s => s.is_current) || courseSessions[0];
    const selectedSessionId = activeSession?.id || '';
    
    // Fetch course fee matching the selected session
    const courseFee = await fetchCourseFeeForSession(courseId, selectedSessionId, isLateral);
    
    console.log('Course changed:', courseId, 'Session:', activeSession?.name, 'Fee:', courseFee);
    
    // Re-calculate consultant payout with new course
    const consultantPayout = calculateConsultantPayout(formData.consultant_id, courseId, isLateral);
    
    setFormData({
      ...formData,
      course_id: courseId,
      booking_amount: amountManuallyEdited ? formData.booking_amount : bookingAmount,
      course_fee: feeManuallyEdited ? formData.course_fee : courseFee,
      consultant_payout: consultantPayout,
      session_id: selectedSessionId,
      session: activeSession?.name || ''
    });
    
    console.log('✅ Auto-selected session:', activeSession?.name, '| Manual edit preserved:', amountManuallyEdited);
  };

  const handleEntryTypeChange = async (entryType) => {
    const isLateral = entryType === 'Lateral';
    
    // Re-fetch booking amount with new entry type if course is already selected
    let newBookingAmount = 0;
    if (formData.course_id) {
      const amountSetting = bookingAmountSettings.find(setting => setting.course_id === formData.course_id);
      if (amountSetting) {
        if (isLateral) {
          newBookingAmount = amountSetting.lateral_amount || amountSetting.amount || 0;
        } else {
          newBookingAmount = amountSetting.regular_amount || amountSetting.amount || 0;
        }
      }
      console.log('Entry type changed, new booking amount:', newBookingAmount);
    }
    
    // Re-fetch course fee for new entry type (session-aware)
    let newCourseFee = formData.course_fee || 0;
    if (formData.course_id) {
      newCourseFee = await fetchCourseFeeForSession(formData.course_id, formData.session_id, isLateral);
    }
    
    // Re-calculate consultant payout with new entry type
    const newConsultantPayout = calculateConsultantPayout(formData.consultant_id, formData.course_id, isLateral);
    
    setFormData({
      ...formData,
      entry_type: entryType,
      is_lateral_entry: isLateral,
      entry_semester: isLateral ? 3 : 1,
      booking_amount: amountManuallyEdited ? formData.booking_amount : newBookingAmount,
      course_fee: feeManuallyEdited ? formData.course_fee : newCourseFee,
      consultant_payout: newConsultantPayout
    });
  };

  const calculateConsultantPayout = (consultantId, courseId, isLateral = false) => {
    if (!consultantId || !courseId) return 0;
    
    const consultant = consultants.find(c => c.id === consultantId);
    if (!consultant || !consultant.course_payout_settings) {
      console.log('No consultant or payout settings found');
      return 0;
    }
    
    // Find payout setting for the selected course
    const payoutSetting = consultant.course_payout_settings.find(
      setting => setting.course_id === courseId
    );
    
    if (!payoutSetting) {
      console.log('No payout setting for this course');
      return 0;
    }
    
    // Return appropriate payout based on entry type
    const payout = isLateral ? payoutSetting.lateral_payout : payoutSetting.regular_payout;
    console.log('Consultant payout calculated:', payout, 'for', isLateral ? 'Lateral' : 'Regular');
    return payout || 0;
  };

  const handleConsultantChange = (consultantId) => {
    const payout = calculateConsultantPayout(consultantId, formData.course_id, formData.is_lateral_entry);
    setFormData({
      ...formData,
      consultant_id: consultantId,
      consultant_payout: payout
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate payment modes
    if (!formData.payment_modes || formData.payment_modes.length === 0) {
      alert('Please add at least one payment mode');
      return;
    }
    
    const totalPaid = formData.payment_modes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
    if (Math.abs(totalPaid - formData.booking_amount) > 0.01) {
      alert(`Payment breakdown total (₹${totalPaid.toLocaleString('en-IN')}) must equal booking amount (₹${formData.booking_amount.toLocaleString('en-IN')})`);
      return;
    }
    
    setLoading(true);
    try {
      console.log('Submitting booking data:', formData);
      
      let response;
      if (isEditMode && editBookingId) {
        // Update existing booking
        response = await axios.put(`${API_URL}/api/seat-booking/${editBookingId}`, { ...formData, is_custom_fee: isCustomFee }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Booking updated:', response.data);
        alert('Seat booking updated successfully!');
      } else {
        // Create new booking
        response = await axios.post(`${API_URL}/api/seat-booking/`, { ...formData, is_custom_fee: isCustomFee }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Booking created:', response.data);
        
        // Show success message only - receipt can be printed from Booking Report
        alert('✅ Seat booked successfully!\n\nReceipt No: ' + (response.data.receipt_no || response.data.booking_id) + '\n\nYou can print the receipt from Seat Booking Report.');
      }
      setFormData({
        booking_id: '',
        receipt_no: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        whatsapp_no: '',
        course_id: '',
        booking_amount: 0,
        course_fee: 0,
        payment_mode: '',
        consultant_id: '',
        session: '',
        session_id: '',
        is_lateral_entry: false,
        entry_type: 'Regular',
        entry_semester: null
      });
      setAmountManuallyEdited(false);
      
      // Ask if user wants to go to booking list
      if (window.confirm('Do you want to view all bookings?')) {
        navigate('/seat-booking/list');
      }
    } catch (error) {
      console.error('Error booking seat - Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to book seat: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Edit Seat Booking' : 'Book Seat'}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isEditMode ? 'Update booking details' : 'Reserve a seat for prospective students'}
          </p>
        </div>
        <button
          onClick={() => navigate('/seat-booking/list')}
          className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          View All Bookings
        </button>
      </div>

      {/* Booking Form */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Booking ID Display */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booking ID <span className="text-xs text-green-600">(Auto-generated)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-blue-700">{formData.booking_id}</span>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Receipt number will be generated after booking</p>
                </div>
                <div className="text-right">
                  <label className="block text-xs text-gray-500 mb-1">Booking Date</label>
                  <input
                    type="date"
                    data-testid="booking-date-input"
                    value={formData.booking_date}
                    onChange={(e) => setFormData({...formData, booking_date: e.target.value})}
                    max={new Date().toISOString().split('T')[0]}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Student Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Student Information
                </h3>
                {/* Name Fields - Split into First, Middle, Last */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.middle_name}
                      onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                      placeholder="Middle name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="email"
                      required
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="student@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="tel"
                      required
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="9876543210"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number * 
                    <span className="text-xs text-green-600 ml-1">(For sending login credentials)</span>
                  </label>
                  <div className="relative">
                    <svg className="absolute left-3 top-2.5 h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <input
                      type="tel"
                      required
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      value={formData.whatsapp_no}
                      onChange={(e) => setFormData({ ...formData, whatsapp_no: e.target.value })}
                      placeholder="9876543210"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Student ID & Password will be sent to this number</p>
                </div>
              </div>

              {/* Booking Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Booking Details
                </h3>
                
                {/* STEP 1: Course */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.course_id}
                    onChange={(e) => handleCourseChange(e.target.value)}
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name} ({course.code})</option>
                    ))}
                  </select>
                </div>

                <MethodSubjectField
                  courseId={formData.course_id}
                  courses={courses}
                  value={formData.method_subject}
                  onChange={(val) => setFormData(prev => ({ ...prev, method_subject: val }))}
                />

                {/* STEP 2: Session */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.session_id}
                    onChange={async (e) => {
                      const sessionId = e.target.value;
                      const selectedSession = sessions.find(s => s.id === sessionId);
                      const isLateral = formData.entry_type === 'Lateral';
                      const newFee = feeManuallyEdited ? formData.course_fee : await fetchCourseFeeForSession(formData.course_id, sessionId, isLateral);
                      setFormData({ 
                        ...formData, 
                        session_id: sessionId,
                        session: selectedSession?.name || '',
                        course_fee: newFee
                      });
                    }}
                  >
                    <option value="">Select Session</option>
                    {filterSessionsByCourse(sessions, formData.course_id).map(session => (
                      <option 
                        key={session.id} 
                        value={session.id}
                        className={session.is_current ? 'font-bold text-green-700' : ''}
                      >
                        {session.is_current ? `✓ ${session.name} (Current)` : session.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* STEP 3: Entry Type (Regular/Lateral) - Only show Lateral if course allows */}
                {(() => {
                  const selectedCourse = courses.find(c => c.id === formData.course_id);
                  if (!formData.course_id) {
                    return (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-500 text-center">Please select a course first</p>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Entry Type *</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => handleEntryTypeChange('Regular')}
                          className={`p-3 border-2 rounded-lg text-center transition-colors ${
                            formData.entry_type === 'Regular'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                        >
                          <div className="font-semibold">Regular</div>
                          <div className="text-xs mt-1">1st Semester</div>
                        </button>
                        {selectedCourse?.lateral_entry_allowed ? (
                          <button
                            type="button"
                            onClick={() => handleEntryTypeChange('Lateral')}
                            className={`p-3 border-2 rounded-lg text-center transition-colors ${
                              formData.entry_type === 'Lateral'
                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                : 'border-gray-300 hover:border-orange-300'
                            }`}
                          >
                            <div className="font-semibold">Lateral Entry</div>
                            <div className="text-xs mt-1">{selectedCourse?.lateral_entry_semester || 3}rd Semester</div>
                          </button>
                        ) : (
                          <div className="p-3 border-2 border-gray-200 rounded-lg text-center bg-gray-50">
                            <div className="font-semibold text-gray-400">Lateral Entry</div>
                            <div className="text-xs mt-1 text-gray-400">Not available</div>
                          </div>
                        )}
                      </div>
                      {formData.entry_type === 'Lateral' && selectedCourse?.lateral_entry_allowed ? (
                        <p className="text-xs text-orange-600 mt-1">
                          💡 Lateral Entry: Admission in {selectedCourse?.lateral_entry_semester || 3}rd Semester
                        </p>
                      ) : (
                        <p className="text-xs text-blue-600 mt-1">
                          💡 Regular: Admission in 1st Semester
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* STEP 4: Course Fee (Auto-fetch, Editable) */}
                {formData.course_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Fee
                    {isCustomFee && (
                      <span data-testid="custom-fee-badge" className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold align-middle">Custom fee</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.course_fee || 0}
                      onChange={(e) => { setFeeManuallyEdited(true); setIsCustomFee(true); setFormData({ ...formData, course_fee: parseFloat(e.target.value) || 0 }); }}
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Auto-populated from course settings, can be changed</p>
                </div>
                )}

                {/* STEP 4.5: Booking Amount (Auto-fetch, Editable) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Booking Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.booking_amount}
                      onChange={(e) => {
                        setFormData({ ...formData, booking_amount: parseFloat(e.target.value) || 0 });
                        setAmountManuallyEdited(true);
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  {formData.course_id && formData.booking_amount > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      Auto-populated from settings ({formData.entry_type}), can be changed
                    </p>
                  )}
                  {formData.course_id && formData.booking_amount === 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      No booking amount configured for this course ({formData.entry_type}). Enter manually or configure in Master Settings.
                    </p>
                  )}
                </div>

                {/* STEP 4.5: Payment Breakdown */}
                <div className="col-span-2">
                  <PaymentBreakdown
                    totalAmount={formData.booking_amount}
                    paymentModes={formData.payment_modes}
                    onChange={(modes) => setFormData({ ...formData, payment_modes: modes })}
                    label="Payment Breakdown *"
                  />
                </div>

                {/* STEP 5: Consultant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consultant (Optional)</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.consultant_id}
                    onChange={(e) => handleConsultantChange(e.target.value)}
                  >
                    <option value="">Select Consultant</option>
                    {consultants.map(consultant => (
                      <option key={consultant.id} value={consultant.id}>{consultant.name}</option>
                    ))}
                  </select>
                </div>

                {/* STEP 6: Consultant Payout (Auto-fetch, Editable) */}
                {formData.consultant_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Payout</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={formData.consultant_payout}
                        onChange={(e) => setFormData({ ...formData, consultant_payout: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Auto-populated from payout settings, can be changed</p>
                  </div>
                )}

              </div>
            </div>

            {/* Preview */}
            {formData.course_id && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Booking Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Student:</span> {`${formData.first_name} ${formData.middle_name} ${formData.last_name}`.trim()}</div>
                  <div><span className="font-medium">Course:</span> {getCourseName(formData.course_id)}</div>
                  <div><span className="font-medium">Contact:</span> {formData.email}</div>
                  <div><span className="font-medium">Amount:</span> ₹{formData.booking_amount.toLocaleString('en-IN')}</div>
                  {formData.is_lateral_entry && (
                    <>
                      <div className="col-span-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Lateral Entry - Starting from Semester {formData.entry_semester}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/seat-booking/list')}
                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                {loading ? (isEditMode ? 'Updating...' : 'Booking...') : (isEditMode ? 'Update Booking' : 'Book Seat')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookSeat;